import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // opens the dedicated sign-in page, returning to the current page afterwards
  const login = (mode) => {
    const p = window.location.pathname;
    const next = p === "/login" || p === "/" ? "/studio" : p;
    window.location.href = `/login?next=${encodeURIComponent(next)}${mode === "register" ? "&mode=register" : ""}`;
  };

  // Google sign-in in a small popup over the site; falls back to a full redirect if popups are blocked
  const googleLogin = (next = "/studio") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const authUrl = (path) => `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + path)}`;
    const w = 480, h = 640;
    const left = window.screenX + (window.outerWidth - w) / 2, top = window.screenY + (window.outerHeight - h) / 2;
    const pop = window.open(authUrl("/auth-popup"), "vitled-google", `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,status=no`);
    if (!pop) { window.location.href = authUrl(next); return; }
    const onMsg = async (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== "vitled-auth") return;
      window.removeEventListener("message", onMsg);
      await checkAuth();
      if (e.data.ok) window.location.assign(next);
    };
    window.addEventListener("message", onMsg);
  };

  // returns {verify, challenge_id, email} when an emailed code is required, else logs in directly
  const emailAuth = async (mode, payload) => {
    const { data } = await api.post(mode === "register" ? "/auth/register" : "/auth/login", payload);
    if (data.verify) return data;
    setUser(data.user);
    return data.user;
  };
  const verifyCode = async (challenge_id, code) => {
    const { data } = await api.post("/auth/verify-code", { challenge_id, code });
    setUser(data.user);
    return data.user;
  };
  const resetPassword = async (challenge_id, code, password) => {
    const { data } = await api.post("/auth/reset-password", { challenge_id, code, password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, login, googleLogin, emailAuth, verifyCode, resetPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
